import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Custom hook for landing page translations using the new modular i18n structure
 * This hook maintains compatibility with the existing LanguageContext system
 * @param {string} page - The landing page type ('advanced', 'basic', 'native', 'complete')
 * @returns {object} Translation function and i18n instance
 */
export const useLandingTranslation = (page) => {
  const namespace = page; // Just use the page name directly
  const { t, i18n, ready } = useTranslation(namespace, { useSuspense: false });
  const { language } = useLanguage(); // Get current language from LanguageContext

  // Sync react-i18next with LanguageContext
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useEffect(() => {
    // Ensure the namespace is loaded
    if (!i18n.hasResourceBundle(i18n.language, namespace)) {
      i18n.loadNamespaces(namespace);
    }
  }, [i18n, namespace]);

  // Helper function to translate with fallback and interpolation support
  const translate = (key, options = {}) => {
    if (!ready) return options.fallback || '';
    
    // If options is a string, treat it as fallback for backward compatibility
    if (typeof options === 'string') {
      const translation = t(key);
      return translation === key ? options : translation;
    }
    
    // Extract fallback from options if provided
    const { fallback, ...interpolationParams } = options;
    
    // Call the underlying t function with interpolation parameters
    const translation = t(key, interpolationParams);
    return translation === key ? (fallback || '') : translation;
  };

  return {
    t: translate,
    i18n,
    ready,
    changeLanguage: i18n.changeLanguage,
    currentLanguage: i18n.language
  };
};